import { Injectable, inject } from '@angular/core';
import { LoanCalculatorService } from './loan-calculator.service';
import { FormatService } from './format.service';
import { AiInsight, InsightParams, LOAN_CONSTRAINTS } from './models';

const MAX_TERM = LOAN_CONSTRAINTS.term.max; // 30

@Injectable({ providedIn: 'root' })
export class AiAdvisorService {
  private calc = inject(LoanCalculatorService);
  private fmt = inject(FormatService);


  generate(p: InsightParams): AiInsight[] {
    const insights: AiInsight[] = [];

    // ── 1. AGE-BASED TENURE ────────────────────────────────────────────────
    if (p.borrowerAge > 0) {
      const retirementAge = 60;
      // Cap suggested tenure to both (retirement - age) AND the 30-yr hard max
      const maxYears = Math.min(MAX_TERM, Math.max(1, retirementAge - p.borrowerAge));
      const yrsOver  = (p.borrowerAge + p.loanTermYears) - retirementAge;

      if (yrsOver > 0) {
        // Only show action button if the suggested tenure is less than current
        // (edge case: age 25 → maxYears 30 = same as current 30 → no button needed)
        const canReduce = maxYears < p.loanTermYears;
        const idealEMI  = this.calc.computeEMI(p.loanAmount, p.annualInterestRate, maxYears);
        insights.push({
          type: 'warning',
          icon: '&#9888;&#65039;',
          title: 'Loan runs past retirement',
          message: `At age ${p.borrowerAge} with a ${p.loanTermYears}-year loan, you'll be repaying until age ${p.borrowerAge + p.loanTermYears}. Consider reducing tenure to ${maxYears} years — ideal EMI: ${this.fmt.currency(idealEMI)}.`,
          ...(canReduce && {
            actionLabel: `Set tenure to ${maxYears} yr`,
            actionKey:   'setTenure',
            actionValue: maxYears
          })
        });
      } else if (yrsOver < -5) {
        insights.push({
          type: 'success',
          icon: '&#9989;',
          title: 'Debt-free before retirement',
          message: `You'll clear this loan ${-yrsOver} years before retirement at age ${retirementAge}. Excellent financial planning!`
        });
      } else {
        insights.push({
          type: 'info',
          icon: '&#8505;&#65039;',
          title: 'Loan clears near retirement',
          message: `You'll be debt-free around age ${p.borrowerAge + p.loanTermYears}. Consider prepayments to finish a few years earlier.`
        });
      }
    }

    // ── 2. EMI AFFORDABILITY ───────────────────────────────────────────────
    if (p.monthlyIncome > 0) {
      const emiRatio = (p.emi / p.monthlyIncome) * 100;

      if (emiRatio > 50) {
        // Solve for tenure that brings EMI to 40% of income using binary search
        // (avoids the unstable log formula which breaks with extreme inputs)
        const targetEMI = p.monthlyIncome * 0.40;
        let safeYears = p.loanTermYears;
        for (let y = p.loanTermYears + 1; y <= MAX_TERM; y++) {
          if (this.calc.computeEMI(p.loanAmount, p.annualInterestRate, y) <= targetEMI) {
            safeYears = y;
            break;
          }
        }
        // If extending to MAX_TERM still doesn't bring it down, use MAX_TERM
        if (this.calc.computeEMI(p.loanAmount, p.annualInterestRate, safeYears) > targetEMI) {
          safeYears = MAX_TERM;
        }

        const safeEMI     = this.calc.computeEMI(p.loanAmount, p.annualInterestRate, safeYears);
        const canExtend   = safeYears > p.loanTermYears;
        insights.push({
          type: 'warning',
          icon: '&#9888;&#65039;',
          title: 'EMI too high',
          message: `Your EMI is ${emiRatio.toFixed(0)}% of monthly income — recommended ≤ 40%. ${canExtend ? `Extending to ${safeYears} years brings EMI to ${this.fmt.currency(safeEMI)}.` : `Even at the maximum ${MAX_TERM}-year tenure your EMI remains high — consider a lower loan amount.`}`,
          ...(canExtend && {
            actionLabel: `Set tenure to ${safeYears} yr`,
            actionKey:   'setTenure',
            actionValue: safeYears
          })
        });

      } else if (emiRatio > 40) {
        insights.push({
          type: 'warning',
          icon: '&#9888;&#65039;',
          title: 'EMI near safe limit',
          message: `EMI is ${emiRatio.toFixed(0)}% of income — just at the 40% threshold. Monitor monthly budget carefully.`
        });

      } else if (emiRatio < 25) {
        const surplusMonthly = Math.round(p.monthlyIncome * 0.10);
        insights.push({
          type: 'tip',
          icon: '&#128161;',
          title: 'Surplus income detected',
          message: `Your EMI is only ${emiRatio.toFixed(0)}% of income. Adding ${this.fmt.currency(surplusMonthly)}/month as prepayment could cut significant interest.`,
          actionLabel: 'Add prepayment',
          actionKey:   'addPrepayment',
          actionValue: { month: 12, amount: surplusMonthly }
        });

      } else {
        insights.push({
          type: 'success',
          icon: '&#9989;',
          title: 'Healthy EMI ratio',
          message: `EMI is ${emiRatio.toFixed(0)}% of income — comfortably within the 40% safety threshold.`
        });
      }
    }

    // ── 3. LOAN-TO-INCOME RATIO ────────────────────────────────────────────
    if (p.monthlyIncome > 0) {
      const annualIncome = p.monthlyIncome * 12;
      const lti = p.loanAmount / annualIncome;
      if (lti > 5) {
        const maxLoan = annualIncome * 5;
        insights.push({
          type: 'warning',
          icon: '&#9888;&#65039;',
          title: 'High loan-to-income ratio',
          message: `Loan is ${lti.toFixed(1)}× your annual income. The safe threshold is 5×. Consider a loan ≤ ${this.fmt.currency(maxLoan)}.`,
          actionLabel: `Set loan to ${this.fmt.currency(maxLoan)}`,
          actionKey:   'setLoanAmount',
          actionValue: maxLoan
        });
      }
    }

    // ── 4. INTEREST BURDEN ─────────────────────────────────────────────────
    const ratio = p.totalInterest / p.loanAmount;
    if (ratio > 1) {
      const prepayAmt = Math.round(p.emi * 2);
      insights.push({
        type: 'warning',
        icon: '&#9888;&#65039;',
        title: 'Interest exceeds principal',
        message: `You'll pay ${this.fmt.currency(p.totalInterest)} in interest on a ${this.fmt.currency(p.loanAmount)} loan (${(ratio * 100).toFixed(0)}% of principal). A prepayment of ${this.fmt.currency(prepayAmt)} now can save significantly.`,
        actionLabel: 'Add prepayment',
        actionKey:   'addPrepayment',
        actionValue: { month: 12, amount: prepayAmt }
      });
    } else if (ratio > 0.5) {
      const prepayAmt = Math.round(p.emi);
      insights.push({
        type: 'info',
        icon: '&#8505;&#65039;',
        title: 'Significant interest outgo',
        message: `Total interest is ${(ratio * 100).toFixed(0)}% of loan amount. Yearly prepayments can reduce this considerably.`,
        actionLabel: 'Add prepayment',
        actionKey:   'addPrepayment',
        actionValue: { month: 12, amount: prepayAmt }
      });
    }

    // ── 5. RATE NEGOTIATION ────────────────────────────────────────────────
    if (p.annualInterestRate > 9) {
      const altRate  = p.annualInterestRate - 0.5;
      const altEMI   = this.calc.computeEMI(p.loanAmount, altRate, p.loanTermYears);
      const altResult = this.calc.buildSchedule(
        p.loanAmount, altRate, p.loanTermYears, altEMI, [], p.prepaymentOption, p.startDate
      );
      const saving = p.totalInterest - altResult.totalInterest;
      insights.push({
        type: 'tip',
        icon: '&#128161;',
        title: 'Rate above 9% — negotiate!',
        message: `At ${altRate}% interest you would save ${this.fmt.currency(saving)} over the loan tenure. Talk to your bank about rate reduction.`,
        actionLabel: `Try ${altRate}% rate`,
        actionKey:   'setRate',
        actionValue: altRate
      });
    }

    // ── 6. OPTIMAL TENURE ─────────────────────────────────────────────────
    // Only fire when tenure is meaningfully above 20 (≥ 25 yr) AND reducing is possible
    if (p.loanTermYears >= 25) {
      const targetYears = 20;
      const simEMI      = this.calc.computeEMI(p.loanAmount, p.annualInterestRate, targetYears);
      const simResult   = this.calc.buildSchedule(
        p.loanAmount, p.annualInterestRate, targetYears, simEMI, [], p.prepaymentOption, p.startDate
      );
      const saving = p.totalInterest - simResult.totalInterest;
      insights.push({
        type: 'tip',
        icon: '&#128161;',
        title: 'Shorter tenure saves more',
        message: `Reducing to ${targetYears} years saves ${this.fmt.currency(saving)} in interest. Your EMI would increase to ${this.fmt.currency(simEMI)}.`,
        actionLabel: `Set tenure to ${targetYears} yr`,
        actionKey:   'setTenure',
        actionValue: targetYears
      });
    }

    // ── 7. PREPAYMENT NUDGE ────────────────────────────────────────────────
    if (!p.hasPrepayments && p.totalInterest > 500000) {
      const prepayAmt = Math.round(p.emi);
      const baseEMI   = this.calc.computeEMI(p.loanAmount, p.annualInterestRate, p.loanTermYears);
      const simResult = this.calc.buildSchedule(
        p.loanAmount, p.annualInterestRate, p.loanTermYears, baseEMI,
        [{ id: 0, month: 12, amount: prepayAmt }],
        p.prepaymentOption, p.startDate
      );
      const monthsSaved    = p.baseTotalMonths - simResult.months.length;
      const interestSaved  = p.totalInterest - simResult.totalInterest;
      insights.push({
        type: 'tip',
        icon: '&#128161;',
        title: 'One extra EMI saves a lot',
        message: `Adding one extra EMI (${this.fmt.currency(prepayAmt)}) at month 12 saves ${this.fmt.currency(interestSaved)} and cuts ${monthsSaved} months from your loan.`,
        actionLabel: 'Add this prepayment',
        actionKey:   'addPrepayment',
        actionValue: { month: 12, amount: prepayAmt }
      });
    }

    return insights;
  }
}
